/* eslint-disable @nx/enforce-module-boundaries */
import {
  Avatar,
  AvatarGroup,
  Box,
  Divider,
  HStack,
  Heading,
  Link,
  LinkButton,
  SeeMore,
  Spinner,
  Stack,
  Text,
  Tooltip,
} from '@mybridge/ui';
import moment from 'moment';
import { forwardRef, useContext, useEffect, useRef, useState } from 'react';
import { MyProfileContext } from '../context';
import { ShowAllLink } from '../show-all-link';
import { MyTestimonialsUserCard } from 'v4/snippets/profile/testimonials/user-card';
import { getUserFullName, getUserProfilePic } from 'v4/lib/commons';
import { ChatOutLined, HandShakeSimpleIcon, SimpleCloseIcon } from '@mybridge/icons';
import { MyNetworkContext } from 'v4/components/network/context';
import { useRouter } from 'next/router';
import { Flex, IconButton, useDisclosure, VStack } from '@chakra-ui/react';
import { useSelector } from 'react-redux';
import { useTranslator } from '@mybridge/ui/custom-hooks/useTranslation';
import { GlobalMatrixPopup } from 'v4/snippets/globalMatrixPopup';
import { getMatrixUserId, getRoomIdFromLS } from 'v4/utils/utils';

export const MyProfileTestimonial = forwardRef(({ ...props }, ref) => {
  const {
    myTestimonials,
    refetchMyTestimonials,
    myTestimonialsEndorseLoading,
  } = useContext(MyProfileContext);
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [selectedUser, setSelectedUser] = useState(null);
  const [confirmAction, setConfirmAction] = useState(() => () => {});
  const { t } = useTranslator();

  useEffect(() => {
    refetchMyTestimonials?.();
  }, []);

  const [isPopupOpen, setIsPopupOpen] = useState(false);
  const router = useRouter();

  const [loadingButtonId, setLoadingButtonId] = useState(null);

  // Modify the handshake handler
  const handleHandshake = async (user) => {
    setLoadingButtonId(user.id);
    try {
      await handshakeUser?.(user);
    } finally {
      setLoadingButtonId(null);
    }
  };

  const { 
    isFriend,
    handshakeUser,
    followUser,
    isHandshakeRequested,
    isFollowed,
    handshakeLoading,
    handleHandshakeSendRevoke, 
  } = useContext(MyNetworkContext);

  const { userProfileInfo } = useSelector((state) => state?.userProfile);



  // Add a ref to track the trigger element
const triggerRef = useRef(null);

// Function to calculate position
const getPopupPosition = () => {
  if (!triggerRef.current) return {};
  
  const triggerRect = triggerRef.current.getBoundingClientRect();
  const viewportHeight = window.innerHeight;
  const spaceBelow = viewportHeight - triggerRect.bottom;
  const spaceAbove = triggerRect.top;
  
  // If more space below, show popup below
  if (spaceBelow >= 400 || spaceBelow > spaceAbove) {
    return {
      top: triggerRect.bottom + 8,
      left: triggerRect.left
    };
  }
  
  // Show popup above if more space there
  return {
    bottom: viewportHeight - triggerRect.top + 8,
    left: triggerRect.left
  };
};

  const timerRef = useRef(null);

  const handleMouseEnter = (testimonial) => {
    if (testimonial?.agreed_user?.length > 0) {
      clearTimeout(timerRef.current);
      setIsPopupOpen(true);
    }
  };

  const handleMouseLeave = () => {
    timerRef.current = setTimeout(() => {
      setIsPopupOpen(false);
    }, 800);
  };


  return (
    <>
      <Stack>
        <HStack alignItems="flex-start">
          <Stack flex={1}>
            <Heading size="sm" fontWeight="400" color="brandPrimary.500">{t('profileTestimonials.testimonials') || "Testimonials"}</Heading>
          </Stack>
          <HStack justifyContent="flex-end">
            <LinkButton href="/profile/testimonials">
              {t('profileTestimonials.manageTestimonials') || "Manage Testimonials"}
            </LinkButton>
          </HStack>
        </HStack>
        {myTestimonials?.length > 0 ? (
          <>
            <Stack pt={4} spacing={6}>
              {myTestimonials
                ?.filter?.((_, ind) => ind < 3)
                ?.map?.((ms, msIndex) => (
                  <MyTestimonialsUserCard
                    key={msIndex}
                    message={ms?.testimonial}
                    user={ms?.giver}
                    createdAt={ms?.created_at}
                  >
                    {ms?.agreed_user_count ? (
                      <HStack pl={8}
                        w={'max-content'}
                        ref={triggerRef}
                      >
                        <HStack
                          onMouseEnter={() => handleMouseEnter(ms)}
                          onMouseLeave={handleMouseLeave}
                        >
                          <AvatarGroup>
                          {ms?.agreed_user
                            ?.filter?.((_, ind) => ind < 3)
                            ?.map?.((u, uind) => (
                              <Avatar
                                key={uind}
                                name={getUserFullName(u)}
                                src={getUserProfilePic(u)}
                                size="xs"
                                as={Link}
                                href={'/p/' + u?.slug}
                              />
                            ))}
                        </AvatarGroup>
                        <Text fontSize="xs">
                          {ms?.agreed_user?.length > 3
                            ? `+${ms.agreed_user?.length - 3} `
                            : ''}{' '}
                          {t('post.agreed') || "Agreed"}
                        </Text>
                        </HStack>
                      </HStack>
                    ) : (
                      ''
                    )}
                  </MyTestimonialsUserCard>
                ))}
            </Stack>
            {myTestimonials?.length > 2 ? (
              <ShowAllLink href="/profile/testimonials" />
            ) : (
              ''
            )}
          </>
        ) : (
          <>
            <HStack justifyContent="center">
              <Text size="sm" color="blackAlpha.500" as={Link} href="/profile/testimonials">
              {t('profileTestimonials.clickHereToGrab') || "Click here to grab testimonials from your friends..."}
              </Text>
            </HStack>
          </>
        )}
      </Stack>
      {isPopupOpen && (
          <Box
            position="fixed"
            maxH="600px"
            w="400px"
            bg="white"
            boxShadow="lg"
            borderRadius="8px"
            box-shadow = "0px 0px 10px 0px #00000033"
            p={4}
            pt={0}
            zIndex={10}
            style={getPopupPosition()}
            onMouseEnter={() => clearTimeout(timerRef.current)}
            onMouseLeave={handleMouseLeave}
          >
            
            <Box position="relative" border="transparent">
            <Divider mb={4} border="transparent"/>
            { 
            <Box>
              <Flex justifyContent="space-between" alignItems="center" mb={4}>
                <Text fontWeight="400" fontSize="20px" color="#3D5A80" display="flex" justifyContent="center" width="100%">
                  {t('profileSkills.agreedUsers') || "Agreed Users"}
                </Text>
                <IconButton
                  display={"flex"}
                  icon={<SimpleCloseIcon height="14px" width="14px" />}
                  aria-label="Close"
                  variant={'unstyled'}
                  onClick={() => setIsPopupOpen(false)}
                />
              </Flex>
              <Box maxHeight="300px" overflowY="auto" p={2}>
              <VStack spacing={4} align="stretch">
                    {
                      myTestimonials?.map((testimonial) => (
                        testimonial?.agreed_user?.map((user, index) => (
                        <Flex key={index} alignItems="center" justifyContent="space-between">
                          <HStack spacing={3} cursor="pointer" onClick={() => userProfileInfo?.id === user?.id ? router.push(`/profile/me`) : router.push(`/p/${user?.slug}`)}>
                            <Avatar src={user?.profile_pic} name={getUserFullName(user)} />
                            <Text fontWeight="500" color="#5B5B5B" fontSize="15px">{getUserFullName(user)}</Text>
                          </HStack>
                          <HStack spacing={2}>
                          {userProfileInfo?.id === user?.id ? null :
                          !isFriend(user) ? <HStack>
                            {isHandshakeRequested?.(user) ? (
                              <Tooltip label={t('publicProfile.withdrawHandshake') || "Withdraw Handshake"}>
                                <IconButton
                                  onClick={(e) =>
                                    handleHandshakeSendRevoke?.(user)
                                  }
                                  _hover={{ bg: 'gray.100' }}
                                  isLoading={handshakeLoading}
                                  // isDisabled={isHandshakeRequested?.(user)}
                                  
                                  icon={
                                    <HandShakeSimpleIcon
                                      color="#F5AEAD"
                                      width="20"
                                      height="20"
                                    />
                                  }
                                  w={'41px'}
                                  height={'41px'}
                                  border={'1px solid #00000033'}
                                  cursor="pointer"
                                  display="flex"
                                  alignItems="center"
                                  justifyContent="center"
                                  borderRadius={'50%'}
                                  
                                  variant="none"
                                />

                              </Tooltip>
                            ) : (
                              <Tooltip zIndex="99999999" label={t('publicProfile.sendHandshakeRequest') || "Send Handshake Request"}>
                                <IconButton
                                  w={'41px'}
                                  _hover={{ bg: 'gray.100' }}
                                  height={'41px'}
                                  border={'1px solid #00000033'}
                                  cursor="pointer"
                                  display="flex"
                                  alignItems="center"
                                  justifyContent="center"
                                  borderRadius={'50%'}
                                  variant="none"
                                  // onClick={(e) => handshakeUser?.(user)}
                                  // isLoading={handshakeLoading}
                                  onClick={() => handleHandshake(user)}
                                  isLoading={loadingButtonId === user.id}
                                  // w="70px"
                                  // isDisabled={isHandshakeRequested?.(user)}
                                  icon={
                                    <HandShakeSimpleIcon
                                      color="#94B096"
                                      width="20"
                                      height="20"
                                    />
                                  }
                                />
                              </Tooltip>
                            )}
                          </HStack> :
                          <Box
                          w={'41px'}
                          height={'41px'}
                          border={'1px solid #00000033'}
                          cursor="pointer"
                          display="flex"
                          alignItems="center"
                          justifyContent="center"
                          borderRadius={'50%'}
                          // onClick={(e) => {
                          //   router.push(`/profile/message?user=${user?.matrix_user_id}`)
                          // }}
                          onClick={() => {
                            // const matrixUserId = getMatrixUserId(user?.id);
                            
                            // if (!(user?.connection?.chat_room_id || matrixUserId)) {
                            //   setConfirmAction(() => (newId) => {
                            //     router.push(`/profile/message?user=${newId}`);
                            //   });
                            //   onOpen();
                            // } else {
                            //   router.push(`/profile/message?user=${user?.matrix_user_id || matrixUserId}`);
                            // }
                            const roomId = getRoomIdFromLS(user?.id);
                            router.push(`/profile/message?user=${roomId}`);
                          }}
                          _hover={{ bg: 'gray.100' }}
                        >
                          <ChatOutLined/>
                        </Box>
                          }
                          </HStack>
                        </Flex>
                        ))
                      ))}
                  </VStack>
              </Box>
            </Box>
          
            }
            </Box>
          </Box>
        )}
        <GlobalMatrixPopup
          isOpen={isOpen}
          onClose={onClose}
          onConfirm={confirmAction}
          selectedUserId={selectedUser?.id}
        />
    </>
  );
});
